import { useCallback, useEffect, useRef, useState } from 'react';

// Fixed UI elements occupy roughly top: 10px to top: 50px in the viewport
const FIXED_UI_ZONE_BOTTOM = 52;

export type HeaderStickyState = {
    /**
     * Whether the header is currently in the fixed UI collision zone
     * (where it would overlap with SidebarTrigger or SyncStatus)
     */
    isInCollisionZone: boolean;
    /**
     * Whether the header is currently in its sticky position (being held by CSS sticky)
     */
    isSticky: boolean;
};

export type UseHeaderStickyStateOptions = {
    /**
     * The top offset where the header becomes sticky (CSS top value in pixels)
     * @default 0
     */
    stickyTop?: number;
};

/**
 * Hook to detect if a header element is in the fixed UI collision zone.
 *
 * A header needs padding when its top position is within the zone where
 * fixed UI elements (SidebarTrigger, SyncStatus) exist (~0-52px from top).
 *
 * This is true whether the header is:
 * - Naturally at the top of the page (not scrolled yet)
 * - Being held at its sticky position due to scrolling
 */
export function useHeaderStickyState(
    headerRef: React.RefObject<HTMLElement | null>,
    options: UseHeaderStickyStateOptions = {},
): HeaderStickyState {
    const { stickyTop = 0 } = options;
    const [state, setState] = useState<HeaderStickyState>({
        isInCollisionZone: false,
        isSticky: false,
    });
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    const calculateState = useCallback(() => {
        if (!headerRef.current)
            return;

        const headerRect = headerRef.current.getBoundingClientRect();

        // Header is in collision zone when its top is within the fixed UI area
        // This is independent of sticky state - applies to both natural position and sticky
        const isInCollisionZone = headerRect.top < FIXED_UI_ZONE_BOTTOM;

        // Header is sticky when it's at its sticky position AND has been scrolled to
        const isAtStickyPosition = Math.abs(headerRect.top - stickyTop) <= 1;
        const hasScrolled = window.scrollY > 0;
        const isSticky = isAtStickyPosition && hasScrolled;

        setState((prev) => {
            if (prev.isInCollisionZone === isInCollisionZone && prev.isSticky === isSticky) {
                return prev;
            }
            return { isInCollisionZone, isSticky };
        });
    }, [headerRef, stickyTop]);

    useEffect(() => {
        const header = headerRef.current;
        if (!header)
            return;

        // Create a sentinel element for IntersectionObserver
        if (!sentinelRef.current) {
            const sentinel = document.createElement('div');
            sentinel.style.cssText = `position: absolute; top: 0; left: 0; right: 0; height: 1px; pointer-events: none; visibility: hidden;`;
            sentinel.setAttribute('data-sticky-sentinel', 'true');

            if (header.parentElement) {
                const parentStyle = window.getComputedStyle(header.parentElement);
                if (parentStyle.position === 'static') {
                    header.parentElement.style.position = 'relative';
                }

                const existingSentinel = header.parentElement.querySelector('[data-sticky-sentinel]');
                if (existingSentinel) {
                    sentinelRef.current = existingSentinel as HTMLDivElement;
                }
                else {
                    header.parentElement.insertBefore(sentinel, header);
                    sentinelRef.current = sentinel;
                }
            }
        }

        // Use IntersectionObserver for efficient detection
        observerRef.current = new IntersectionObserver(
            () => {
                calculateState();
            },
            {
                rootMargin: `-${stickyTop + 1}px 0px 0px 0px`,
                threshold: 0,
            },
        );

        if (sentinelRef.current) {
            observerRef.current.observe(sentinelRef.current);
        }

        // Initial calculation
        calculateState();

        return () => {
            observerRef.current?.disconnect();
            if (sentinelRef.current && sentinelRef.current.parentElement) {
                sentinelRef.current.parentElement.removeChild(sentinelRef.current);
                sentinelRef.current = null;
            }
        };
    }, [headerRef, stickyTop, calculateState]);

    // Listen to scroll for immediate updates
    useEffect(() => {
        const handleScroll = () => {
            calculateState();
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [calculateState]);

    return state;
}
